/**
 * Angebote, Auftragsbestätigungen, Rechnungen.
 *
 * Was ein Beleg gerade zulässt, sagt der Server mit: `aenderbar`,
 * `herausgebbar`, `stornierbar`. Die Oberfläche baut die Regeln nicht
 * nach — sonst liefen sie auseinander, und der Benutzer sähe Knöpfe, die
 * der Server ablehnt, oder keine, wo er dürfte.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'

export type Belegart = 'angebot' | 'auftragsbestaetigung' | 'rechnung' | 'storno'

export type Belegstatus =
  | 'entwurf'
  | 'herausgegeben'
  | 'angenommen'
  | 'abgelehnt'
  | 'bezahlt'
  | 'storniert'

export const BELEGARTTEXT: Record<Belegart, string> = {
  angebot: 'Angebot',
  auftragsbestaetigung: 'Auftragsbestätigung',
  rechnung: 'Rechnung',
  storno: 'Stornorechnung',
}

export const BELEGSTATUSTEXT: Record<Belegstatus, string> = {
  entwurf: 'Entwurf',
  herausgegeben: 'Herausgegeben',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
  bezahlt: 'Bezahlt',
  storniert: 'Storniert',
}

export type Position = {
  id: string
  position: number
  bezeichnung: string
  beschreibung: string | null
  menge: string
  einheit: string
  einzelpreis: string
  steuersatz: string
  spielplatz_id: string | null
  /** Gesetzt heißt: diese Zeile rechnet genau diese Prüfung ab. */
  pruefung_id: string | null
  summe: string
}

export type BelegZeile = {
  id: string
  art: Belegart
  status: Belegstatus
  nummer: string | null
  datum: string
  betreff: string | null
  kunde_id: string
  kunde_name: string
  netto: string
  brutto: string
  faellig_am: string | null
  bezahlt_am: string | null
  ueberfaellig: boolean
}

export type Beleg = BelegZeile & {
  einleitung: string | null
  schluss: string | null
  bestellnummer: string | null
  leistung_von: string | null
  leistung_bis: string | null
  zahlungsziel_tage: number
  steuer: string
  kleinunternehmer: boolean
  herausgegeben_am: string | null
  vorgaenger_id: string | null
  pruefung_id: string | null
  positionen: Position[]
  /** Vom Server bestimmt – die Oberfläche entscheidet das nicht selbst. */
  aenderbar: boolean
  herausgebbar: boolean
  stornierbar: boolean
}

export function euro(betrag: string | number): string {
  return Number(betrag).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  })
}

export function useBelege(filter?: { status?: string; art?: string; kunde_id?: string }) {
  const teile = new URLSearchParams()
  for (const [k, v] of Object.entries(filter ?? {})) if (v) teile.set(k, v)
  const anhang = teile.toString() ? `?${teile}` : ''

  return useQuery({
    queryKey: ['belege', anhang] as const,
    queryFn: ({ signal }) => api.holen<BelegZeile[]>(`/api/belege${anhang}`, signal),
  })
}

export function useBeleg(id: string) {
  return useQuery({
    queryKey: ['beleg', id] as const,
    queryFn: ({ signal }) => api.holen<Beleg>(`/api/belege/${id}`, signal),
    enabled: Boolean(id),
  })
}

function neuLaden(speicher: ReturnType<typeof useQueryClient>, id?: string) {
  void speicher.invalidateQueries({ queryKey: ['belege'] })
  if (id) void speicher.invalidateQueries({ queryKey: ['beleg', id] })
}

export function useBelegAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, unknown>) =>
      api.senden<Beleg>('/api/belege', daten),
    onSuccess: () => neuLaden(speicher),
  })
}

export function useBelegAendern(id: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, unknown>) =>
      api.aendern<Beleg>(`/api/belege/${id}`, daten),
    onSuccess: () => neuLaden(speicher, id),
  })
}

export function useBelegLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/belege/${id}`),
    onSuccess: () => neuLaden(speicher),
  })
}

export function usePositionAnlegen(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, unknown>) =>
      api.senden<Beleg>(`/api/belege/${belegId}/positionen`, daten),
    onSuccess: () => neuLaden(speicher, belegId),
  })
}

export function usePositionAendern(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Record<string, unknown>) =>
      api.aendern<Beleg>(`/api/belege/${belegId}/positionen/${id}`, daten),
    onSuccess: () => neuLaden(speicher, belegId),
  })
}

export function usePositionLoeschen(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.loeschen(`/api/belege/${belegId}/positionen/${id}`),
    onSuccess: () => neuLaden(speicher, belegId),
  })
}

/** Herausgeben, stornieren, übernehmen, bezahlt melden. */
export function useBelegSchritt(belegId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ schritt, daten }: { schritt: string; daten?: unknown }) =>
      api.senden<Beleg>(`/api/belege/${belegId}/${schritt}`, daten ?? {}),
    onSuccess: () => neuLaden(speicher, belegId),
  })
}


/**
 * Die Rechnungsübersicht: ein Kunde, eine Zeile.
 *
 * Die Belegliste ist nach Datum geordnet und sagt, was zuletzt passiert
 * ist. Diese hier sagt, wie es um einen Kunden steht – und das ist die
 * häufigere Frage.
 */
export type KundenUebersicht = {
  kunde_id: string
  kunde_name: string
  kundennummer: string | null
  entwuerfe: number
  offen_anzahl: number
  offen_betrag: string
  ueberfaellig_anzahl: number
  ueberfaellig_betrag: string
  letzte_rechnung_am: string | null
  /** Abgeschlossene Prüfungen, die auf keiner Rechnung stehen. */
  offene_pruefungen: number
}

export function useBelegUebersicht(rest?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['belege', 'uebersicht'] as const,
    queryFn: ({ signal }) =>
      api.holen<KundenUebersicht[]>('/api/belege/uebersicht', signal),
    enabled: rest?.enabled ?? true,
  })
}
